import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { CATEGORY_ALL_ID } from 'features/rewards/constants/constants';
import useTopCategories from 'features/rewards/hooks/usePartnerOffersCategories';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const CategoriesScreenV2 = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();

  const { defaultTitle, defaultMessage, doneButtonTitle } =
    useDefaultErrorStrings();
  const { categoriesResponse, loading, error } = useTopCategories();

  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'rewardsCategories',
  });

  const logErrorAnalytics = usePressableAnalytics({
    analyticsId: 'categoryPageError',
    customAttributes: {
      error_description: error?.message ?? '',
    },
  });

  useEffect(() => {
    if (error) {
      logErrorAnalytics();
    }
  }, [error, logErrorAnalytics]);

  return (
    <View style={[styles.mainContainer, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('categoriesText'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <FlatList
        data={categoriesResponse}
        ItemSeparatorComponent={() => (
          <View style={styles.dividerContainer}>
            <HairLineDivider />
          </View>
        )}
        ListHeaderComponent={() => (
          <>
            <SectionDivider labelText={translatedText('allCategoriesText')} />
            <ListItem
              key="view-all-categories-item"
              primaryCopy={translatedText('all')}
              leadingAccessory={{
                id: CATEGORY_ALL_ID,
                name: VXLComponentId.ICON_AVATAR,
                props: {
                  iconKey: 'CASHBACK',
                  variant: 'MD',
                },
              }}
              onPress={() => {
                router.push({
                  pathname: '/rewards/categories/[category]',
                  params: {
                    category: CATEGORY_ALL_ID,
                    name: translatedText('all'),
                  },
                });
              }}
              analyticsId="categoryOfferTypeSelected"
              customAttributes={{ offer_type: 'All offers' }}
            />
            <View style={styles.dividerContainer}>
              <HairLineDivider />
            </View>
          </>
        )}
        renderItem={({ item }) => (
          <ListItem
            key={item.order}
            primaryCopy={item.name}
            leadingAccessory={{
              id: item.id,
              name: VXLComponentId.ICON_AVATAR,
              props: {
                iconKey: item.iconKey ?? 'ELLIPSE',
                iconColor: item.iconKey
                  ? theme.color.text.primary
                  : theme.color.interactive.secondaryPressed,
                variant: 'MD',
              },
            }}
            onPress={() => {
              router.push({
                pathname: '/rewards/categories/[category]',
                params: {
                  category: item.id,
                  name: item.name,
                },
              });
            }}
            analyticsId="categoryOfferTypeSelected"
            customAttributes={{ offer_bracket: item.name.toLowerCase() }}
          />
        )}
        keyExtractor={(item) => item.id}
        removeClippedSubviews={false}
      />

      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={defaultTitle}
              messageBody={defaultMessage}
              primaryButton={{
                title: doneButtonTitle,
                onPress: () => router.navigate('/rewards'),
              }}
              screenAnalyticsEventKey="rewardsError"
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  dividerContainer: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
  mainContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default CategoriesScreenV2;
